export type RgbColor = { r: number; g: number; b: number };

/**
 * Converts a hex color string into an RGB color object.
 *
 * Supported input formats:
 * - `#RRGGBB`
 * - `RRGGBB`
 * - `#RGB`
 * - `RGB`
 *
 * Shorthand 3-digit hex values are expanded before parsing:
 * - `#FFF` -> `#FFFFFF`
 * - `09c` -> `0099cc`
 *
 * @param hex - The hex color string to convert.
 * @returns An RGB object when the input is valid, otherwise `null`.
 */
export function hexToRgb(hex: string): RgbColor | null {
  // Remove the optional leading "#" so the value can be normalized and validated.
  const normalizedHex = hex.replace(/^#/, "");

  // Expand 3-digit shorthand hex colors into their 6-digit equivalent.
  // Example: "ABC" becomes "AABBCC".
  const expandedHex =
    normalizedHex.length === 3
      ? normalizedHex
          .split("")
          .map((char) => char + char)
          .join("")
      : normalizedHex;

  // Validate the expanded value and capture the red, green, and blue pairs.
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(expandedHex);

  // Convert each two-character hex pair into a base-10 RGB channel value.
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

/**
 * Calculates the relative luminance of an RGB color using the WCAG formula.
 *
 * Relative luminance is a normalized brightness value in the range 0..1,
 * where 0 is darkest and 1 is lightest. This value is used to calculate
 * contrast ratios between colors.
 *
 * Reference:
 * http://www.w3.org/TR/2008/REC-WCAG20-20081211/#relativeluminancedef
 *
 * @param color - The RGB color to evaluate.
 * @returns The relative luminance value.
 */
function getLuminance(color: RgbColor): number {
  // Store gamma-corrected channel values after normalization.
  const correctedColor: RgbColor = { r: 0, g: 0, b: 0 };

  Object.entries(color).forEach(([key, colorValue]) => {
    // Normalize from 0..255 into 0..1.
    let rgb = colorValue / 255;

    // Apply WCAG gamma correction to convert sRGB to linear light.
    rgb = rgb <= 0.03928 ? rgb / 12.92 : Math.pow((rgb + 0.055) / 1.055, 2.4);

    correctedColor[key as keyof RgbColor] = rgb;
  });

  // Combine corrected channels using the WCAG luminance coefficients.
  return (
    0.2126 * correctedColor.r +
    0.7152 * correctedColor.g +
    0.0722 * correctedColor.b
  );
}

/**
 * Calculates the contrast ratio between two luminance values.
 *
 * The ratio is always expressed as lighter / darker and is therefore always >= 1.
 *
 * @param L1 - The luminance of the first color.
 * @param L2 - The luminance of the second color.
 * @returns The contrast ratio.
 */
function getContrastRatio(L1: number, L2: number): number {
  const lum1 = L1 + 0.05;
  const lum2 = L2 + 0.05;

  return lum1 > lum2 ? lum1 / lum2 : lum2 / lum1;
}

/**
 * Calculates the contrast ratio between two hex color strings.
 *
 * Both colors may be provided in 3-digit or 6-digit hex format, with or
 * without a leading `#`.
 *
 * @param hex1 - The first hex color.
 * @param hex2 - The second hex color.
 * @returns The contrast ratio, or `null` if either color is invalid.
 */
export function getContrastRatioFromHexColors(
  hex1: string,
  hex2: string
): number | null {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);

  if (rgb1 && rgb2) {
    const lum1 = getLuminance(rgb1);
    const lum2 = getLuminance(rgb2);

    return getContrastRatio(lum1, lum2);
  }

  return null;
}

/**
 * Checks the contrast ratio between a background color and a preferred text color against WCAG AA standards.
 *
 * This function evaluates the contrast ratio between a background color and a preferred text color.
 * If the preferred text color does not meet the WCAG AA minimum contrast ratio (4.5:1 for normal text),
 * a warning is logged for debugging and design review.
 *
 * @param backgroundHex - The background color in hex format (e.g., "#FFFFFF").
 * @param textHex - The preferred text color in hex format to test first.
 * @returns An object containing the selected text color and its contrast ratio, or `null` if any input color is invalid.
 */
export function checkColorContrast({
  backgroundColor,
  textColor,
}: {
  backgroundColor: string;
  textColor: string;
}): { contrast: number; textColor: string } | null {
  const minimumContrast = 4.5;

  // First check whether the caller-provided text color already satisfies
  // the minimum accessibility requirement.
  const contrast = getContrastRatioFromHexColors(backgroundColor, textColor);
  if (contrast === null) {
    return null;
  }

  if (contrast >= minimumContrast) {
    return { contrast, textColor: textColor };
  }

  // Warn when none of the tested text colors meets the WCAG AA minimum.
  if (contrast < minimumContrast) {
    console.warn(
      "[checkColorContrast] None of the tested text colors meets the minimum contrast ratio of 4.5:1.",
      {
        backgroundColor,
        providedTextColor: textColor,
        providedTextContrast: contrast,
        minimumContrast,
      }
    );
  }

  return { contrast, textColor: textColor };
}
